import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    DISCORD_URL,
    QUICK_START_URL,
    // TALLY_FORM_ID
} from 'consts';
import { useAppDispatch, useAppSelector } from 'hooks';
import { goToUrl } from 'libs';
import { ROUTES } from 'routes';
import { useGetProjectsQuery } from 'services/project';
import { useGetRunsQuery } from 'services/run';
import { useGetUserBillingInfoQuery } from 'services/user';

import { openTutorialPanel, selectTutorialPanel, selectUserName, updateTutorialPanelState } from 'App/slice';

import { useSideNavigation } from '../hooks';
import {
    BILLING_TUTORIAL,
    CONFIGURE_CLI_TUTORIAL,
    CREATE_FIRST_PROJECT,
    // CREDITS_TUTORIAL,
    JOIN_DISCORD_TUTORIAL,
    QUICKSTART_TUTORIAL,
} from './constants';

import { ITutorialItem } from 'App/types';

export const useTutorials = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { billingUrl } = useSideNavigation();
    const useName = useAppSelector(selectUserName);
    const {
        billingCompleted,
        createProjectCompleted,
        configureCLICompleted,
        discordCompleted,
        tallyCompleted,
        quickStartCompleted,
        hideStartUp,
    } = useAppSelector(selectTutorialPanel);

    const { data: userBillingData } = useGetUserBillingInfoQuery({ username: useName ?? '' }, { skip: !useName });
    const { data: projectData } = useGetProjectsQuery({});
    const { data: runsData } = useGetRunsQuery({
        limit: 1,
    });

    const completeIsChecked = useRef<boolean>(false);

    useEffect(() => {
        if (
            userBillingData &&
            projectData?.data &&
            runsData &&
            !completeIsChecked.current &&
            location.pathname !== ROUTES.PROJECT.ADD
        ) {
            const billingCompleted = userBillingData.balance > 0;
            const configureCLICompleted = runsData.length > 0;
            const createProjectCompleted = projectData.data.length > 0;

            let tempHideStartUp = hideStartUp;

            if (hideStartUp === null) {
                tempHideStartUp = billingCompleted && configureCLICompleted && createProjectCompleted;
            }

            // Set hideStartUp without updating localstorage
            dispatch(
                updateTutorialPanelState({
                    billingCompleted,
                    configureCLICompleted,
                    createProjectCompleted,
                    hideStartUp: tempHideStartUp,
                }),
            );

            if (!tempHideStartUp && process.env.UI_VERSION === 'sky') {
                dispatch(openTutorialPanel());
            }

            completeIsChecked.current = true;
        }
    }, [userBillingData, runsData, projectData, location.pathname]);

    useEffect(() => {
        if (projectData?.data && projectData.data.length > 0 && !createProjectCompleted) {
            dispatch(
                updateTutorialPanelState({
                    createProjectCompleted: true,
                }),
            );
        }
    }, [projectData]);

    const startBillingTutorial = useCallback(() => {
        navigate(billingUrl);
    }, [billingUrl]);

    const finishBillingTutorial = useCallback(() => {
        dispatch(updateTutorialPanelState({ billingCompleted: true }));
    }, []);

    const startFirstProjectTutorial = useCallback(() => {
        navigate(ROUTES.PROJECT.ADD);
    }, []);

    const finishFirstProjectTutorial = useCallback(() => {
        dispatch(updateTutorialPanelState({ createProjectCompleted: true }));
    }, []);

    const startConfigCliTutorial = useCallback(() => {
        if (projectData?.data?.length) {
            navigate(ROUTES.PROJECT.DETAILS.SETTINGS.FORMAT(projectData.data[0].project_name));
        }
    }, [projectData]);

    const finishConfigCliTutorial = useCallback(() => {
        dispatch(updateTutorialPanelState({ configureCLICompleted: true }));
    }, []);

    const startDiscordTutorial = useCallback(() => {
        goToUrl(DISCORD_URL, true);
        dispatch(updateTutorialPanelState({ discordCompleted: true }));
    }, []);

    const startQuickStartTutorial = useCallback(() => {
        goToUrl(QUICK_START_URL, true);
        dispatch(updateTutorialPanelState({ quickStartCompleted: true }));
    }, []);

    // const startCreditsTutorial = useCallback(() => {
    //     if (typeof Tally !== 'undefined') {
    //         Tally.openPopup(TALLY_FORM_ID);
    //         dispatch(updateTutorialPanelState({ tallyCompleted: true }));
    //     }
    // }, []);

    const tutorials = useMemo<ITutorialItem[]>(() => {
        return [
            // {
            //     ...CREDITS_TUTORIAL,
            //     id: 1,
            //     startWithoutActivation: true,
            //     completed: tallyCompleted,
            //     startCallback: startCreditsTutorial,
            // },

            {
                ...CREATE_FIRST_PROJECT,
                id: 2,
                completed: createProjectCompleted,
                startCallback: startFirstProjectTutorial,
                finishCallback: finishFirstProjectTutorial,
            },

            {
                ...CONFIGURE_CLI_TUTORIAL,
                id: 3,
                completed: configureCLICompleted,
                startCallback: startConfigCliTutorial,
                finishCallback: finishConfigCliTutorial,
                prerequisitesNeeded: !createProjectCompleted,
            },

            {
                ...QUICKSTART_TUTORIAL,
                id: 5,
                startWithoutActivation: true,
                completed: quickStartCompleted,
                startCallback: startQuickStartTutorial,
            },

            {
                ...JOIN_DISCORD_TUTORIAL,
                id: 6,
                startWithoutActivation: true,
                completed: discordCompleted,
                startCallback: startDiscordTutorial,
            },

            {
                ...BILLING_TUTORIAL,
                id: 4,
                completed: billingCompleted,
                startCallback: startBillingTutorial,
                finishCallback: finishBillingTutorial,
            },
        ];
    }, [
        billingUrl,
        createProjectCompleted,
        quickStartCompleted,
        discordCompleted,
        tallyCompleted,
        billingCompleted,
        configureCLICompleted,
        finishFirstProjectTutorial,
        finishBillingTutorial,
        finishConfigCliTutorial,
    ]);

    return { tutorials } as const;
};
